import React, { useCallback, useMemo } from "react";
import UserAvatar from "@/components/controls/UserAvatar";
import Text from "@/components/ui/text";
import { find, map, max } from "lodash";
import DrilldownChart from "./DrilldownChart";
import { StageAverageDurationChartDataType } from "@/components/layouts/StageAverageDuration/StageAverageDuration";
import { useStageDurationBreadcrumbs } from "@/components/layouts/StageAverageDuration/StageDurationBreadcrumbsContext";
import { reducePrecision } from "@/utils/numbers";
import { cn } from "@/utils/utils";
import DrilldownSkeleton from "@/components/ui/drilldown-skeleton";
import { daysToWeeks } from "date-fns";

interface Props {
  selectedElement?: StageAverageDurationChartDataType;
  selectedStageId: string;
  selectedTab: string;
}

const DurationDrilldown: React.FC<Props> = ({
  selectedElement,
  selectedStageId,
  selectedTab
}) => {
  const { dynamicTableData, handleRowClick, directReportsData, isLoading } =
    useStageDurationBreadcrumbs();

  const getDrilldownData = useCallback(
    (userId: string): number => {
      const directReport = find(
        directReportsData?.directReports,
        (report) => report.id === userId
      );

      if (!directReport || !selectedStageId) return 0;

      // Determine which collection to search based on the selected tab
      const data =
        selectedTab === "stages"
          ? directReport.stages
          : directReport.milestones;

      // Find the matching stage/milestone
      const match = find(
        data,
        (phase) => phase.id.toString() === selectedStageId
      );

      return reducePrecision(
        daysToWeeks(match?.averageTimeSpentValues.you || 0)
      );
    },
    [directReportsData, selectedStageId, selectedTab]
  );

  const drilldownChartData = useMemo(
    () =>
      map(dynamicTableData, (directReport) => ({
        start: 0,
        duration: getDrilldownData(directReport.id)
      })),
    [dynamicTableData, getDrilldownData]
  );

  const maxValue = useMemo(
    () => max(map(drilldownChartData, "duration")) || 0,
    [drilldownChartData]
  );

  return (
    <div
      className={cn(
        "bg-surface-elevation0 flex flex-col py-6 gap-10 min-h-full w-full",
        isLoading ? "gap-8" : "gap-2"
      )}
    >
      {map(dynamicTableData, (directReport) =>
        isLoading ? (
          <DrilldownSkeleton key={directReport.id} />
        ) : (
          <div
            key={directReport.id}
            className="flex gap-2 px-4 py-1 max-h-12 items-center"
            onClick={() => handleRowClick(directReport)}
          >
            <div className="w-52 h-fit">
              <div className="flex gap-2 w-fit px-2 py-1 rounded-lg cursor-pointer hover:bg-surface-hover">
                <UserAvatar userData={directReport} size="M" />
                <Text as="div" variant="body">
                  {directReport?.displayName}
                </Text>
              </div>
            </div>
            <div className="w-[calc(90%-10rem)]">
              <DrilldownChart
                chartData={[
                  { start: 0, duration: getDrilldownData(directReport.id) }
                ]}
                selectedElement={selectedElement}
                maxValue={maxValue}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default DurationDrilldown;
